import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Input from '../components/input/Input';
import CurrencyInput from '../components/input/CurrencyInput';
import DatePicker from '../components/datepicker/DatePicker';
import DropDown from '../components/dropdown/DropDown';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import Checkbox from '../components/checkBox/CheckBox';
import MissingUsdQuotationModal from '../components/modals/MissingUsdQuotationModal';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import {
  getCapitalContributionFormMetadata,
  getCapitalContributionById,
  getReimpositionEligibleContributions,
  createCapitalContribution,
  updateCapitalContribution,
} from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { Toast, useToast } from '../components/toast/toast';
import { reimpositionEligibleLabel } from '../utils/reimpositionEligibleLabel';
import { MSG_MISSING_USD_QUOTATION_FOR_CONTRIBUTIONS } from '../utils/managementAssetRules';
import { isUsdQuotationAvailable } from '../utils/usdQuotationCheck';
import { formatMoney } from '../utils/Masks';

interface MetadataItem {
  id: number;
  name: string;
}

interface CapitalContributionFormMetadata {
  protectors: MetadataItem[];
  currencies: MetadataItem[];
  funds: MetadataItem[];
  previousContributions: MetadataItem[];
}

interface ReimpositionEligibleItem {
  id: number;
  contribution_number?: number | null;
  contributionDate?: string;
  contribution_date?: string;
  authorized_fund_id?: number | null;
  authorizedFundId?: number | null;
  authorized_fund_name?: string | null;
  authorizedFundName?: string | null;
  currency_id?: number;
  currencyId?: number;
  reimposition_balance?: number;
  reimpositionBalance?: number;
  origin_protector_id?: number;
  originProtectorId?: number;
  origin_protector_name?: string | null;
  originProtectorName?: string | null;
}

interface FormValues {
  protector_id: number | null;
  contribution_date: string;
  expiration_date: string;
  currency_id: number | null;
  amount: number | null;
  fixed_available_amount: number | null;
  participant_shareholders_return: boolean;
  is_tax_deductible: boolean;
  authorized_fund_id: number | null;
  previous_contribution_id: number | null;
  minute_date: string;
  minute_number: string;
  is_reimposition: boolean;
}

const WITHDRAWN_OR_INACTIVE_TOAST =
  'El aporte está retirado y no se puede editar.';

const amountBelowIntegratedMsg = (integrated: number) =>
  `El monto no puede ser menor al importe ya integrado: ${formatMoney(integrated)}`;

const isUsdCurrencyName = (currencyName?: string) =>
  (currencyName || '').toUpperCase().includes('USD') ||
  (currencyName || '').toUpperCase().includes('DÓLAR') ||
  (currencyName || '').toUpperCase().includes('DOLAR');

const formatCompactBalance = (value: number, currencyName?: string) => {
  const isUsd = isUsdCurrencyName(currencyName);
  return value.toLocaleString('es-AR', {
    minimumFractionDigits: isUsd ? 2 : 0,
    maximumFractionDigits: isUsd ? 2 : 0,
  });
};

const normalizeEligible = (raw: ReimpositionEligibleItem) => {
  const contributionDate = raw.contribution_date || raw.contributionDate || '';
  const fundName = raw.authorized_fund_name || raw.authorizedFundName || 'Fondo';
  const balance = Number(raw.reimposition_balance ?? raw.reimpositionBalance ?? 0);
  const originProtectorName = raw.origin_protector_name || raw.originProtectorName || '';
  const contributionNumber = raw.contribution_number ?? null;
  return { id: raw.id, contributionNumber, contributionDate, fundName, balance, originProtectorName };
};

const toDateInput = (value?: string | null) => {
  if (!value) return '';
  return value.includes('T') ? value.split('T')[0] : value;
};

const ProtectorContributionNew = () => {
  const navigate = useNavigate();
  const { id, contributionId } = useParams<{ id: string; contributionId?: string }>();
  const isEditMode = Boolean(contributionId);
  const parsedContributionId = contributionId ? parseInt(contributionId, 10) : null;
  const parsedProtectorId = id ? parseInt(id, 10) : null;

  const [metadata, setMetadata] = useState<CapitalContributionFormMetadata | null>(null);
  const [eligibleReimpositions, setEligibleReimpositions] = useState<ReturnType<typeof normalizeEligible>[]>([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [integratedAmount, setIntegratedAmount] = useState(0);
  const [initialSnapshot, setInitialSnapshot] = useState<FormValues | null>(null);
  const [preloadDone, setPreloadDone] = useState(!isEditMode);
  const [redirectingAway, setRedirectingAway] = useState(false);

  const handleGoBack = (toastPayload?: { title: string; description: string }) => {
    const toast =
      toastPayload &&
      typeof toastPayload === 'object' &&
      typeof toastPayload.description === 'string'
        ? toastPayload
        : undefined;
    const navOptions = toast ? { state: { toast } } : undefined;
    if (toast) {
      setRedirectingAway(true);
    }
    if (id) {
      navigate(`/protectores/${id}`, navOptions);
    } else {
      navigate(routes.fundsAndProtectors, navOptions);
    }
  };

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quotationCheckDone, setQuotationCheckDone] = useState(false);
  const [missingUsdQuotationModalOpen, setMissingUsdQuotationModalOpen] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const twoYearsLater = new Date();
  twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);
  const expirationDate = twoYearsLater.toISOString().split('T')[0];

  const [formValues, setFormValues] = useState<FormValues>({
    protector_id: parsedProtectorId,
    contribution_date: today,
    expiration_date: expirationDate,
    currency_id: null,
    amount: null,
    fixed_available_amount: null,
    participant_shareholders_return: false,
    is_tax_deductible: false,
    authorized_fund_id: null,
    previous_contribution_id: null,
    minute_date: today,
    minute_number: '',
    is_reimposition: false,
  });

  useEffect(() => {
    fetchFormMetadata();
    checkUsdQuotationAvailability();
  }, []);

  useEffect(() => {
    if (!isEditMode) return;

    if (
      parsedContributionId == null ||
      Number.isNaN(parsedContributionId) ||
      parsedProtectorId == null ||
      Number.isNaN(parsedProtectorId)
    ) {
      handleGoBack({
        title: 'Error',
        description: 'No se pudo cargar el aporte',
      });
      setPreloadDone(true);
      setLoading(false);
      return;
    }

    const preload = async () => {
      setLoading(true);
      try {
        const response = await getCapitalContributionById(parsedContributionId, parsedProtectorId);
        if (!response.success || !('data' in response) || !response.data) {
          handleGoBack({
            title: 'Error',
            description: 'No se pudo cargar el aporte',
          });
          return;
        }

        const data = response.data;
        const withdrawnAt = data.withdrawn_at ?? data.withdrawnAt ?? null;
        const isActive = data.is_active ?? data.isActive ?? true;
        if (withdrawnAt || !isActive) {
          handleGoBack({
            title: 'Aviso',
            description: WITHDRAWN_OR_INACTIVE_TOAST,
          });
          return;
        }

        const hydrated: FormValues = {
          protector_id: data.protector_id ?? data.protectorId ?? parsedProtectorId,
          contribution_date: toDateInput(data.contribution_date ?? data.contributionDate),
          expiration_date: toDateInput(data.expiration_date ?? data.expirationDate),
          currency_id: data.currency_id ?? data.currencyId ?? null,
          amount: Number(data.amount ?? 0),
          fixed_available_amount: Number(
            data.fixed_available_amount ?? data.fixedAvailableAmount ?? 0,
          ),
          participant_shareholders_return: Boolean(
            data.participant_shareholders_return ?? data.participantShareholdersReturn,
          ),
          is_tax_deductible: Boolean(data.is_tax_deductible ?? data.isTaxDeductible),
          authorized_fund_id: data.authorized_fund_id ?? data.authorizedFundId ?? null,
          previous_contribution_id:
            data.previous_contribution_id ?? data.previousContributionId ?? null,
          minute_date: toDateInput(data.minute_date ?? data.minuteDate) || today,
          minute_number: data.minute_number ?? data.minuteNumber ?? '',
          is_reimposition: Boolean(data.is_reimposition ?? data.isReimposition),
        };

        setFormValues(hydrated);
        setInitialSnapshot(hydrated);
        setIntegratedAmount(
          Number(data.integrated_amount ?? data.integratedAmount ?? 0),
        );
        setPreloadDone(true);
      } catch (error) {
        console.error('Error preloading capital contribution:', error);
        handleGoBack({
          title: 'Error',
          description: 'No se pudo cargar el aporte',
        });
      } finally {
        setLoading(false);
      }
    };

    preload();
  }, [isEditMode, parsedContributionId, parsedProtectorId]);

  const checkUsdQuotationAvailability = async () => {
    try {
      const available = await isUsdQuotationAvailable();
      if (!available) {
        setMissingUsdQuotationModalOpen(true);
      }
    } finally {
      setQuotationCheckDone(true);
    }
  };

  const handleCloseMissingUsdQuotationModal = () => {
    setMissingUsdQuotationModalOpen(false);
    handleGoBack();
  };

  const handleGoToQuotations = () => {
    setMissingUsdQuotationModalOpen(false);
    navigate(routes.quotations);
  };

  const fetchFormMetadata = async () => {
    if (!isEditMode) setLoading(true);
    try {
      const response = await getCapitalContributionFormMetadata();
      if (response.success && 'data' in response) {
        setMetadata(response.data);
      }
    } catch (error) {
      console.error('Error fetching form metadata:', error);
    } finally {
      if (!isEditMode) setLoading(false);
    }
  };

  const updateFormValues = (updates: Partial<FormValues>) => {
    setFormValues(prev => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    if (isEditMode) return;
    if (formValues.contribution_date) {
      const contributionDate = new Date(formValues.contribution_date);
      contributionDate.setFullYear(contributionDate.getFullYear() + 2);
      const newExpirationDate = contributionDate.toISOString().split('T')[0];
      if (formValues.expiration_date !== newExpirationDate) {
        updateFormValues({ expiration_date: newExpirationDate });
      }
    }
  }, [formValues.contribution_date, isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    const loadEligible = async () => {
      if (!formValues.is_reimposition || !formValues.protector_id || !formValues.currency_id) {
        setEligibleReimpositions([]);
        return;
      }
      setLoadingEligible(true);
      try {
        const response = await getReimpositionEligibleContributions(
          formValues.protector_id,
          formValues.currency_id,
        );
        if (response.success && 'data' in response) {
          const list = Array.isArray(response.data) ? response.data : [];
          setEligibleReimpositions(list.map(normalizeEligible));
        } else {
          setEligibleReimpositions([]);
        }
      } catch (error) {
        console.error('Error fetching reimposition-eligible contributions:', error);
        setEligibleReimpositions([]);
      } finally {
        setLoadingEligible(false);
      }
    };
    loadEligible();
  }, [formValues.is_reimposition, formValues.protector_id, formValues.currency_id, isEditMode]);

  useEffect(() => {
    if (isEditMode || !formValues.is_reimposition) return;
    if (!formValues.previous_contribution_id) return;
    const stillThere = eligibleReimpositions.some(e => e.id === formValues.previous_contribution_id);
    if (!stillThere) {
      updateFormValues({ previous_contribution_id: null });
    }
  }, [eligibleReimpositions, formValues.is_reimposition, formValues.previous_contribution_id, isEditMode]);

  const selectedEligible = useMemo(
    () => eligibleReimpositions.find(e => e.id === formValues.previous_contribution_id) || null,
    [eligibleReimpositions, formValues.previous_contribution_id],
  );

  const selectedCurrencyName = metadata?.currencies?.find(c => c.id === formValues.currency_id)?.name;

  const amountBelowIntegrated =
    isEditMode &&
    formValues.amount !== null &&
    formValues.amount < integratedAmount;

  const isDirty = useMemo(() => {
    if (!isEditMode || !initialSnapshot) return true;
    return (
      formValues.amount !== initialSnapshot.amount ||
      formValues.fixed_available_amount !== initialSnapshot.fixed_available_amount ||
      formValues.participant_shareholders_return !==
        initialSnapshot.participant_shareholders_return ||
      formValues.is_tax_deductible !== initialSnapshot.is_tax_deductible ||
      formValues.authorized_fund_id !== initialSnapshot.authorized_fund_id ||
      formValues.minute_date !== initialSnapshot.minute_date ||
      formValues.minute_number !== initialSnapshot.minute_number
    );
  }, [formValues, initialSnapshot, isEditMode]);

  const parseApiError = (errorMessage: string, errorCode?: string): string | null => {
    if (
      errorCode === 'MISSING_USD_QUOTATION' ||
      /MISSING_USD_QUOTATION/i.test(errorMessage)
    ) {
      return MSG_MISSING_USD_QUOTATION_FOR_CONTRIBUTIONS;
    }
    if (/REIMPOSITION_PREVIOUS_REQUIRED/i.test(errorMessage)) {
      return 'Debe seleccionar un aporte anterior para la reimposición';
    }
    if (/PROTECTOR_NOT_ENABLED/i.test(errorMessage)) {
      return 'El protector debe estar habilitado para crear una reimposición';
    }
    if (/CAPITAL_CONTRIBUTION_AMOUNT_BELOW_INTEGRATED/i.test(errorMessage)) {
      return amountBelowIntegratedMsg(integratedAmount);
    }
    if (/CAPITAL_CONTRIBUTION_AMOUNT_NOT_POSITIVE|AMOUNT_NOT_POSITIVE/i.test(errorMessage)) {
      return 'El monto debe ser mayor a 0';
    }
    const pattern = /The sum of active contributions.*would exceed the authorized?/i;
    if (errorMessage.match(pattern)) {
      return 'No se puede integrar aportes porque el monto supera el Riesgo vivo máximo';
    }
    return null;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (isEditMode && parsedContributionId && parsedProtectorId) {
        const requestData = {
          amount: formValues.amount!,
          fixed_available_amount: formValues.fixed_available_amount!,
          participant_shareholders_return: formValues.participant_shareholders_return,
          is_tax_deductible: formValues.is_tax_deductible,
          authorized_fund_id: formValues.authorized_fund_id!,
          minute_date: formValues.minute_date,
          minute_number: formValues.minute_number,
        };
        const response = await updateCapitalContribution(
          parsedContributionId,
          parsedProtectorId,
          requestData,
        );
        if (response.success) {
          navigate(`/protectores/${id}`);
        } else {
          const errorMessage =
            ('message' in response ? response.message : 'Error al actualizar el aporte') ||
            'Error al actualizar el aporte';
          const errorCode = 'code' in response ? response.code : undefined;
          const translatedMessage = parseApiError(errorMessage, errorCode);
          useToast.error({
            title: 'Error',
            description: translatedMessage || errorMessage,
            id: 'default-toast',
            autoClose: 8000,
            closeButton: true,
          });
        }
        return;
      }

      const requestData = {
        protector_id: formValues.protector_id!,
        contribution_date: formValues.contribution_date,
        expiration_date: formValues.expiration_date,
        currency_id: formValues.currency_id!,
        amount: formValues.amount!,
        fixed_available_amount: formValues.fixed_available_amount!,
        participant_shareholders_return: formValues.participant_shareholders_return,
        is_tax_deductible: formValues.is_tax_deductible,
        authorized_fund_id: formValues.authorized_fund_id!,
        previous_contribution_id: formValues.previous_contribution_id,
        minute_date: formValues.minute_date,
        minute_number: formValues.minute_number,
        is_reimposition: formValues.is_reimposition,
      };

      const response = await createCapitalContribution(requestData);
      if (response.success) {
        if (id) {
          navigate(`/protectores/${id}`);
        } else {
          navigate(routes.fundsAndProtectors);
        }
      } else {
        const errorMessage =
          ('message' in response ? response.message : 'Error al crear el aporte') ||
          'Error al crear el aporte';
        const errorCode = 'code' in response ? response.code : undefined;
        const translatedMessage = parseApiError(errorMessage, errorCode);
        useToast.error({
          title: 'Error',
          description: translatedMessage || errorMessage,
          id: 'default-toast',
          autoClose: 8000,
          closeButton: true,
        });
      }
    } catch (error: any) {
      console.error('Error saving capital contribution:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        (isEditMode ? 'Error al actualizar el aporte' : 'Error al crear el aporte');
      const translatedMessage = parseApiError(errorMessage);
      useToast.error({
        title: 'Error',
        description: translatedMessage || errorMessage,
        id: 'default-toast',
        autoClose: 8000,
        closeButton: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currencyItems = metadata?.currencies?.map(c => ({
    label: c.name,
    disabled: isEditMode,
  })) || [];

  const getSelectedCurrencyName = () => {
    const selected = metadata?.currencies?.find(c => c.id === formValues.currency_id);
    return selected ? selected.name : 'Seleccionar';
  };

  const handleCurrencyChange = (selectedLabel: string) => {
    if (isEditMode) return;
    const currency = metadata?.currencies?.find(c => c.name === selectedLabel);
    if (currency) {
      updateFormValues({
        currency_id: currency.id,
        previous_contribution_id: formValues.is_reimposition ? null : formValues.previous_contribution_id,
      });
    }
  };

  const participantShareholdersReturnItems = [
    { label: 'SI', disabled: false },
    { label: 'NO', disabled: false }
  ];

  const getParticipantShareholdersReturnLabel = () => {
    return formValues.participant_shareholders_return ? 'SI' : 'NO';
  };

  const handleParticipantShareholdersReturnChange = (selectedLabel: string) => {
    updateFormValues({ participant_shareholders_return: selectedLabel === 'SI' });
  };

  const isTaxDeductibleItems = [
    { label: 'SI', disabled: false },
    { label: 'NO', disabled: false }
  ];

  const getIsTaxDeductibleLabel = () => {
    return formValues.is_tax_deductible ? 'SI' : 'NO';
  };

  const handleIsTaxDeductibleChange = (selectedLabel: string) => {
    updateFormValues({ is_tax_deductible: selectedLabel === 'SI' });
  };

  const fundItems = metadata?.funds?.map(f => ({
    label: f.name,
    disabled: false
  })) || [];

  const getSelectedFundName = () => {
    const selected = metadata?.funds?.find(f => f.id === formValues.authorized_fund_id);
    return selected ? selected.name : 'Seleccionar';
  };

  const handleFundChange = (selectedLabel: string) => {
    const fund = metadata?.funds?.find(f => f.name === selectedLabel);
    if (fund) {
      updateFormValues({ authorized_fund_id: fund.id });
    }
  };

  const eligibleLabel = (pc: ReturnType<typeof normalizeEligible>) =>
    reimpositionEligibleLabel({
      contributionNumber: pc.contributionNumber!,
      formattedBalance: formatCompactBalance(pc.balance, selectedCurrencyName),
    });

  const reimpositionEligibleForDropdown = eligibleReimpositions.filter(
    (pc) => pc.contributionNumber != null,
  );

  const previousContributionItems = formValues.is_reimposition
    ? reimpositionEligibleForDropdown.map(pc => ({
        id: String(pc.id),
        label: eligibleLabel(pc),
        disabled: false,
      }))
    : metadata?.previousContributions?.map(pc => ({
        label: pc.name,
        disabled: false
      })) || [];

  const getSelectedPreviousContributionName = () => {
    if (formValues.is_reimposition) {
      if (!selectedEligible) return loadingEligible ? 'Cargando...' : 'Seleccionar';
      if (selectedEligible.contributionNumber == null) return 'Seleccionar';
      return eligibleLabel(selectedEligible);
    }
    const selected = metadata?.previousContributions?.find(pc => pc.id === formValues.previous_contribution_id);
    return selected ? selected.name : 'Seleccionar';
  };

  const handlePreviousContributionChange = (selectedLabel: string, item?: { id?: string; label: string }) => {
    if (formValues.is_reimposition) {
      if (item?.id) {
        updateFormValues({ previous_contribution_id: Number(item.id) });
        return;
      }
      const previousContribution = reimpositionEligibleForDropdown.find(
        (pc) => eligibleLabel(pc) === selectedLabel,
      );
      if (previousContribution) {
        updateFormValues({ previous_contribution_id: previousContribution.id });
      }
      return;
    }
    const previousContribution = metadata?.previousContributions?.find(pc => pc.name === selectedLabel);
    if (previousContribution) {
      updateFormValues({ previous_contribution_id: previousContribution.id });
    }
  };

  const handleReimpositionToggle = (checked: boolean) => {
    updateFormValues({
      is_reimposition: checked,
      previous_contribution_id: null,
    });
  };

  const isFormValid = () => {
    const baseValid =
      formValues.protector_id !== null &&
      formValues.contribution_date !== '' &&
      formValues.expiration_date !== '' &&
      formValues.currency_id !== null &&
      formValues.amount !== null &&
      formValues.amount > 0 &&
      formValues.fixed_available_amount !== null &&
      formValues.authorized_fund_id !== null &&
      formValues.minute_date !== '' &&
      formValues.minute_number !== '';

    if (!baseValid) return false;
    if (amountBelowIntegrated) return false;
    if (isEditMode) return true;
    if (!formValues.is_reimposition) return true;
    if (formValues.previous_contribution_id == null) return false;
    return true;
  };

  const canSubmit = isFormValid() && isDirty && !submitting;

  if (redirectingAway) {
    return null;
  }

  if (loading || !quotationCheckDone || (isEditMode && !preloadDone)) {
    return (
      <Card width='-webkit-fill-available'>
        <Card display='flex' justifyContent='center' alignItems='center' height='60vh'>
          <Spinner />
        </Card>
      </Card>
    );
  }

  if (missingUsdQuotationModalOpen) {
    return (
      <MissingUsdQuotationModal
        isOpen
        onClose={handleCloseMissingUsdQuotationModal}
        onGoToQuotations={handleGoToQuotations}
      />
    );
  }

  return (
    <>
      {isEditMode && (
        <div style={{ width: '85vw' }}>
          <PageBreadcrumb
            items={[
              { label: 'Inicio', to: routes.dashboard },
              { label: 'Fondos y protectores', to: routes.fundsAndProtectors },
              { label: 'Editar aporte' },
            ]}
          />
        </div>
      )}
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      <Card margin={{ bottom: '4' }} display='flex' alignItems='center' gap='16px'>
        <Button
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={() => handleGoBack()}
          aria-label="Volver a configuración"
        />
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          {isEditMode
            ? `Editar aporte n° ${parsedContributionId ?? contributionId}`
            : 'Nuevo aporte'}
        </H>
      </Card>

      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            {isEditMode ? 'Datos del protector' : 'Datos del aporte'}
          </H>
        </Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '8px', alignItems: 'end' }}>
          <div style={{ position: 'relative' }}>
            <CurrencyInput
              label="Monto *"
              value={formValues.amount}
              onValueChange={(val) => updateFormValues({ amount: val })}
              size="md"
              inputStatus={amountBelowIntegrated ? 'error' : 'normal'}
            />
            {amountBelowIntegrated && (
              // Absolute so the error doesn't grow the cell and misalign the row (grid uses alignItems: 'end').
              <P
                withMargin={false}
                type='4'
                style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', color: '#d1493f' }}
              >
                {amountBelowIntegratedMsg(integratedAmount)}
              </P>
            )}
          </div>
          <DropDown
            id="currency"
            label="Moneda *"
            title={getSelectedCurrencyName()}
            items={currencyItems}
            onChange={handleCurrencyChange}
            size="md"
            disabled={isEditMode}
          />
          <div>
            <div style={{ marginBottom: '8px' }}>
              <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                Fecha de contribución *
              </P>
            </div>
            <div style={{ marginTop: '8px' }}>
              <DatePicker
                defaultValue={formValues.contribution_date}
                onChange={(date: string) => {
                  if (!isEditMode) updateFormValues({ contribution_date: date });
                }}
                disabled={isEditMode}
                displayFormat="DD/MM/AA"
              />
            </div>
          </div>
          <div>
            <div style={{ marginBottom: '8px' }}>
              <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                Fecha de expiración *
              </P>
            </div>
            <div style={{ marginTop: '8px' }}>
              <DatePicker
                defaultValue={formValues.expiration_date}
                onChange={() => {}}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </div>
          </div>
        </div>
        <div style={{ marginBottom: '24px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
          <CurrencyInput
            label="Disponible fijo *"
            value={formValues.fixed_available_amount}
            onValueChange={(val) => updateFormValues({ fixed_available_amount: val })}
            size="md"
          />
          <DropDown
            id="participant_shareholders_return"
            label="Rendimiento socios partícipes *"
            title={getParticipantShareholdersReturnLabel()}
            items={participantShareholdersReturnItems}
            onChange={handleParticipantShareholdersReturnChange}
            size="md"
            marginTop="4px"
          />
          <DropDown
            id="is_tax_deductible"
            label="Desgrava Impositivamente *"
            title={getIsTaxDeductibleLabel()}
            items={isTaxDeductibleItems}
            onChange={handleIsTaxDeductibleChange}
            size="md"
            marginTop="4px"
          />
          <DropDown
            id="authorized_fund"
            label="Fondo autorizado *"
            title={getSelectedFundName()}
            items={fundItems}
            onChange={handleFundChange}
            size="md"
            marginTop="4px"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
          <Input
            label="Número de acta *"
            value={formValues.minute_number}
            onChange={(e) => updateFormValues({ minute_number: e.target.value })}
            size="md"
          />
          <div>
            <div style={{ marginBottom: '8px' }}>
              <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                Fecha de acta *
              </P>
            </div>
            <div style={{ marginTop: '8px' }}>
              <DatePicker
                defaultValue={formValues.minute_date}
                onChange={(date: string) => updateFormValues({ minute_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </div>
          </div>
          {!isEditMode && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '100%' }}>
                <div style={{ height: '22px', marginBottom: '4px' }} aria-hidden="true" />
                <Checkbox
                  id="is_reimposition"
                  checked={formValues.is_reimposition}
                  onChange={handleReimpositionToggle}
                  size="md"
                  labelWeight="bold"
                  customStyle={{ uncheckedBackgroundColor: '#FFFFFF' }}
                >
                  Es reimposición
                </Checkbox>
              </div>
              {formValues.is_reimposition ? (
                <DropDown
                  id="previous_contribution"
                  label="Aporte anterior *"
                  title={getSelectedPreviousContributionName()}
                  items={previousContributionItems}
                  onChange={handlePreviousContributionChange}
                  size="md"
                  disabled={!formValues.currency_id || loadingEligible}
                />
              ) : (
                <div />
              )}
            </>
          )}
        </div>
        {!isEditMode && formValues.is_reimposition && formValues.currency_id && !loadingEligible && reimpositionEligibleForDropdown.length === 0 && (
          <P withMargin={false} type='4' style={{ marginTop: '12px', color: '#d1493f' }}>
            No hay aportes retirados con saldo a reimponer en el sistema para la moneda seleccionada.
          </P>
        )}
      </Card>

      <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isEditMode ? 'Guardar cambios' : 'Confirmar creación'}
        </Button>
      </Card>
      <Toast id="default-toast" position="top-right" />
    </Card>
    </>
  );
};

export default ProtectorContributionNew;
export { WITHDRAWN_OR_INACTIVE_TOAST, amountBelowIntegratedMsg };
